import { env } from '@/config/env';
import { getToken, useAuthStore } from '@/stores/auth';
import { getTenant } from '@/stores/tenant';
import { refreshToken } from '@/features/auth/authService';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type ApiOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  /** skip Authorization header (e.g. public endpoints) */
  anonymous?: boolean;
};

/**
 * Thin fetch wrapper. Same-origin => no CORS.
 * Adds Bearer token + X-TenantID, refreshes the token on demand,
 * and normalises errors / JSON parsing.
 */
export async function api<T = unknown>(
  path: string,
  { body, anonymous, headers, ...init }: ApiOptions = {},
): Promise<T> {
  // Keep the token fresh before each call (no-op in mock mode).
  await refreshToken();

  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'application/json');
  finalHeaders.set('X-TenantID', getTenant());

  if (!anonymous) {
    const token = getToken();
    if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData) {
      payload = body;
    } else {
      finalHeaders.set('Content-Type', 'application/json');
      payload = JSON.stringify(body);
    }
  }

  const url = path.startsWith('http') ? path : `${env.apiBase}${path}`;
  const res = await fetch(url, { ...init, headers: finalHeaders, body: payload });

  if (res.status === 401) {
    useAuthStore.getState().reset();
    throw new ApiError(401, 'No autorizado');
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await res.json().catch(() => undefined) : await res.text();

  if (!res.ok) {
    const message =
      (data as { message?: string })?.message ?? `Error ${res.status}`;
    throw new ApiError(res.status, message, data);
  }

  return data as T;
}

export const apiClient = {
  get: <T>(path: string, opts?: ApiOptions) => api<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: ApiOptions) =>
    api<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: ApiOptions) =>
    api<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: ApiOptions) =>
    api<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: ApiOptions) =>
    api<T>(path, { ...opts, method: 'DELETE' }),
};
